import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    AdminRecord,
    AdminActionLog,
    AdminRole,
    listAdmins,
    getAdminRecord,
    setAdminRole,
    lookupAccountByEmail,
    fetchAdminActionLogs,
} from '../../services/adminRoleService';
import './AdminRoles.css';

const ALL_ROLES: { key: AdminRole; label: string; description: string }[] = [
    { key: 'super_admin', label: 'Super Admin', description: 'Full system access, can manage other admins' },
    { key: 'regional_admin', label: 'Regional Admin', description: 'Manage content and users in a specific region' },
    { key: 'content_moderator', label: 'Content Moderator', description: 'Review and moderate posts, podcasts' },
    { key: 'support_agent', label: 'Support Agent', description: 'Handle user support messages' },
];

const COMMON_FEATURES = ['moderation', 'messaging', 'analytics', 'content', 'quiz', 'omore', 'biz'];

export const AdminRoles: React.FC = () => {
    const { currentUser, claims } = useAuth();
    const navigate = useNavigate();

    const [admins, setAdmins] = useState<AdminRecord[]>([]);
    const [selectedAdmin, setSelectedAdmin] = useState<AdminRecord | null>(null);
    const [actionLogs, setActionLogs] = useState<AdminActionLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [showAddForm, setShowAddForm] = useState(false);
    const [lookupEmail, setLookupEmail] = useState('');
    const [lookedUpAccount, setLookedUpAccount] = useState<{ accountId: string; displayName?: string; primaryEmail: string } | null>(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState<string | null>(null);

    const [editRoles, setEditRoles] = useState<AdminRole[]>([]);
    const [editRegion, setEditRegion] = useState('');
    const [editFeatures, setEditFeatures] = useState<string[]>([]);

    const isSuperAdmin = claims?.adminRoles?.includes('super_admin') ?? false;

    const loadAdmins = useCallback(async () => {
        try {
            setLoading(true);
            const list = await listAdmins();
            setAdmins(list);
        } catch (err: any) {
            setError(err.message || 'Failed to load admins');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAdmins(); }, [loadAdmins]);

    const selectAdmin = async (admin: AdminRecord) => {
        setSelectedAdmin(admin);
        setEditRoles([...admin.roles]);
        setEditRegion(admin.adminScope?.region || '');
        setEditFeatures(admin.adminScope?.features || []);
        setError(null);
        setSuccess(null);
        try {
            const logs = await fetchAdminActionLogs(admin.accountId);
            setActionLogs(logs);
        } catch {
            setActionLogs([]);
        }
    };

    const handleLookup = async () => {
        setLookupError(null);
        setLookedUpAccount(null);
        if (!lookupEmail.trim()) return;
        setLookupLoading(true);
        try {
            const account = await lookupAccountByEmail(lookupEmail.trim());
            if (!account) {
                setLookupError('No account found for this email.');
            } else {
                setLookedUpAccount(account);
            }
        } catch (err: any) {
            setLookupError(err.message || 'Lookup failed');
        } finally {
            setLookupLoading(false);
        }
    };

    const handleSaveRoles = async () => {
        if (!selectedAdmin) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const adminScope = {
                region: editRegion.trim() || undefined,
                features: editFeatures.length > 0 ? editFeatures : undefined,
            };
            await setAdminRole({
                targetAccountId: selectedAdmin.accountId,
                roles: editRoles,
                adminScope: (adminScope.region || adminScope.features) ? adminScope : undefined,
            });
            setSuccess('Roles updated successfully.');
            await loadAdmins();
            const updated = await getAdminRecord(selectedAdmin.accountId);
            if (updated) setSelectedAdmin(updated);
        } catch (err: any) {
            setError(err.message || 'Failed to update roles');
        } finally {
            setSaving(false);
        }
    };

    const handleAddAdmin = async () => {
        if (!lookedUpAccount) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await setAdminRole({ targetAccountId: lookedUpAccount.accountId, roles: ['support_agent'] });
            setSuccess(`${lookedUpAccount.primaryEmail} added as Support Agent.`);
            setShowAddForm(false);
            setLookupEmail('');
            setLookedUpAccount(null);
            await loadAdmins();
        } catch (err: any) {
            setError(err.message || 'Failed to add admin');
        } finally {
            setSaving(false);
        }
    };

    const toggleRole = (role: AdminRole) => {
        setEditRoles(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const toggleFeature = (feature: string) => {
        setEditFeatures(prev =>
            prev.includes(feature) ? prev.filter(f => f !== feature) : [...prev, feature]
        );
    };

    const handleRemoveAdmin = async () => {
        if (!selectedAdmin) return;
        if (!window.confirm(`Remove all admin access for ${selectedAdmin.email}?`)) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await setAdminRole({ targetAccountId: selectedAdmin.accountId, roles: [] });
            setSuccess('Admin access removed.');
            setSelectedAdmin(null);
            await loadAdmins();
        } catch (err: any) {
            setError(err.message || 'Failed to remove admin');
        } finally {
            setSaving(false);
        }
    };

    const formatTimestamp = (ts: any) => {
        if (!ts) return '—';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleString();
    };

    return (
        <div className="admin-roles-container">
            <header className="admin-roles-header">
                <button className="back-button" onClick={() => navigate('/dashboard')}>← Dashboard</button>
                <h1>Admin Role Management</h1>
                <span className="current-user">{currentUser?.email}</span>
            </header>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <div className="admin-roles-layout">
                <aside className="admins-list-panel">
                    <div className="panel-header">
                        <h2>Admins ({admins.length})</h2>
                        {isSuperAdmin && (
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => setShowAddForm(v => !v)}
                            >
                                + Add Admin
                            </button>
                        )}
                    </div>

                    {showAddForm && isSuperAdmin && (
                        <div className="add-admin-form">
                            <h3>Add Admin by Email</h3>
                            <div className="lookup-row">
                                <input
                                    type="email"
                                    placeholder="user@example.com"
                                    value={lookupEmail}
                                    onChange={e => setLookupEmail(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleLookup()}
                                    className="lookup-input"
                                />
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleLookup}
                                    disabled={lookupLoading}
                                >
                                    {lookupLoading ? '...' : 'Lookup'}
                                </button>
                            </div>
                            {lookupError && <p className="lookup-error">{lookupError}</p>}
                            {lookedUpAccount && (
                                <div className="lookup-result">
                                    <p><strong>{lookedUpAccount.displayName || lookedUpAccount.primaryEmail}</strong></p>
                                    <p className="text-muted">{lookedUpAccount.primaryEmail}</p>
                                    <p className="text-muted">ID: {lookedUpAccount.accountId}</p>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={handleAddAdmin}
                                        disabled={saving}
                                    >
                                        {saving ? 'Adding...' : 'Add as Support Agent'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <div className="loading">Loading admins...</div>
                    ) : admins.length === 0 ? (
                        <div className="empty">No admins found.</div>
                    ) : (
                        <ul className="admins-list">
                            {admins.map(admin => (
                                <li
                                    key={admin.accountId}
                                    className={`admin-item ${selectedAdmin?.accountId === admin.accountId ? 'selected' : ''}`}
                                    onClick={() => selectAdmin(admin)}
                                >
                                    <div className="admin-name">{admin.displayName || admin.email}</div>
                                    <div className="admin-email">{admin.email}</div>
                                    <div className="admin-roles-badges">
                                        {admin.roles.map(r => (
                                            <span key={r} className={`role-badge role-${r.replace(/_/g, '-')}`}>
                                                {r.replace(/_/g, ' ')}
                                            </span>
                                        ))}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>

                <main className="admin-detail-panel">
                    {!selectedAdmin ? (
                        <div className="no-selection">Select an admin from the list to view or edit their roles.</div>
                    ) : (
                        <>
                            <section className="role-editor">
                                <h2>
                                    {selectedAdmin.displayName || selectedAdmin.email}
                                    <span className="account-id">ID: {selectedAdmin.accountId}</span>
                                </h2>

                                <div className="editor-section">
                                    <h3>Roles</h3>
                                    <div className="roles-grid">
                                        {ALL_ROLES.map(({ key, label, description }) => (
                                            <label key={key} className={`role-checkbox ${!isSuperAdmin ? 'disabled' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={editRoles.includes(key)}
                                                    onChange={() => isSuperAdmin && toggleRole(key)}
                                                    disabled={!isSuperAdmin}
                                                />
                                                <div className="role-info">
                                                    <strong>{label}</strong>
                                                    <span>{description}</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="editor-section">
                                    <h3>Admin Scope</h3>
                                    <div className="scope-editor">
                                        <label className="scope-label">
                                            Region
                                            <input
                                                type="text"
                                                placeholder="e.g. us-east, eu-west (leave blank for global)"
                                                value={editRegion}
                                                onChange={e => setEditRegion(e.target.value)}
                                                disabled={!isSuperAdmin}
                                                className="scope-input"
                                            />
                                        </label>
                                        <div className="scope-features">
                                            <label className="scope-label">Features</label>
                                            <div className="features-grid">
                                                {COMMON_FEATURES.map(feature => (
                                                    <label key={feature} className={`feature-checkbox ${!isSuperAdmin ? 'disabled' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={editFeatures.includes(feature)}
                                                            onChange={() => isSuperAdmin && toggleFeature(feature)}
                                                            disabled={!isSuperAdmin}
                                                        />
                                                        {feature}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isSuperAdmin && (
                                    <div className="editor-actions">
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleSaveRoles}
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={handleRemoveAdmin}
                                            disabled={saving}
                                        >
                                            Remove Admin Access
                                        </button>
                                    </div>
                                )}
                            </section>

                            <section className="action-logs">
                                <h3>Action Log ({actionLogs.length} entries)</h3>
                                {actionLogs.length === 0 ? (
                                    <div className="empty">No action logs found.</div>
                                ) : (
                                    <table className="logs-table">
                                        <thead>
                                            <tr>
                                                <th>Time</th>
                                                <th>Action</th>
                                                <th>Target</th>
                                                <th>Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {actionLogs.map(log => (
                                                <tr key={log.id}>
                                                    <td className="log-time">{formatTimestamp(log.timestamp)}</td>
                                                    <td><span className="log-action">{log.action.replace(/_/g, ' ')}</span></td>
                                                    <td className="log-target">{log.targetAccountId || '—'}</td>
                                                    <td className="log-details">
                                                        {log.details
                                                            ? <pre>{JSON.stringify(log.details, null, 2)}</pre>
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </section>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};
